import { addItem, getItems, markVisited, removeAllItems, removeItem, removeItems } from './http';
import { arraysAreEqual, shouldFetch } from './utils';
import { createLogger } from '@derstandard/logger';
import { trackUserAction } from '$lib/tracking';
import { type Writable, writable, Subscriber, Invalidator, readonly, get } from 'svelte/store';

const log = createLogger('listStore');

/**
 * Creates a store that contains a list of items.
 * The store is automatically synced with other tabs/windows using BroadcastChannels.
 * @param {string} listName - used to name the store and the BroadcastChannel
 * @returns {Writable<ListItem[]>} the store
 */
function createStore(listName: string, isManualReload: boolean): Writable<ListItem[]> {
	// Using custom names for each store avoids interference
	const channel = new BroadcastChannel(listName);
	let initialised: boolean = false;
	let syncState: ListItem[] = [];

	// The sessionStorage sync will be removed later on
	const store = writable<ListItem[]>(sessionStorage.getItem(listName) ? JSON.parse(sessionStorage.getItem(listName)) : []);
	const { subscribe, set, update } = store;

	const useActions = function (item: ListItem | undefined) {
		return {
			add: () => add(item),
			remove: () => remove(item),
			deleteItem: () => deleteItem(item),
			visit: () => visit(item),
			clear: () => clear(),
			removeVisited: () => removeVisited(),
		};
	};

	// Webcomponents inform the store about their actions via messages, which are handled in this function.
	const onMessage = (event: MessageEvent) => {
		event.data.source !== 'react-devtools-content-script' ? log.info('Message received: ', event) : null;
		const actions = useActions(event.data.item);

		if (!Object.keys(actions).includes(event.data.action)) {
			return;
		}

		actions[event.data.action]();
	};

	// Clears sessionStorage-entry when user logs out to prevent wrong states
	const onLogOut = () => {
		sessionStorage.removeItem(listName);
	};

	// When the store of another tab/window changes, a broadcast message is
	// dispatched, which is handled in this function.
	const onBroadcastMessage = (event: MessageEvent) => {
		if (arraysAreEqual(get(store), event.data)) {
			return;
		}
		syncState = event.data;
		set(event.data);
	};

	// Methods to interact with the store
	const storeContains = (item: ListItem) => {
		const { url } = item;
		const items = get(store);
		return items.some((i) => i.url === url);
	};

	const add = async (item: ListItem) => {
		trackUserAction('readlater_add_article');

		if (storeContains(item)) {
			log.error('Store already contains item');
			return;
		}

		if (sessionStorage.getItem(`${listName}-user`) === 'false') {
			sessionStorage.removeItem(`${listName}-user`);
		}

		const previousState = get(store);

		update((items) => [...items, { ...item }]);
		sortOrRevertStore(addItem(item), previousState);
	};

	const remove = (item: ListItem) => {
		if (!storeContains(item)) {
			log.error('Store does not contain item');
			return;
		}

		const previousState = get(store);

		trackUserAction('readlater_remove_article');
		update((items) => items.filter((i) => i.url !== item.url));
		sortOrRevertStore(removeItem(item), previousState);
	};

	const deleteItem = (item: ListItem) => {
		if (!storeContains(item)) {
			log.error('Store does not contain item');
			return;
		}

		const previousState = get(store);

		trackUserAction('readlater_delete_item');
		update((items) => items.filter((i) => i.url !== item.url));
		sortOrRevertStore(removeItem(item), previousState);
	};

	const visit = (item: ListItem) => {
		if (!storeContains(item)) {
			log.error('Store does not contain item');
			return;
		}

		update((items) => items.map((i) => (i.url === item.url ? { ...i, visited: true } : i)));
		markVisited(item);
	};

	const clear = () => {
		trackUserAction('readlater_remove_all');
		sortOrRevertStore(removeAllItems(), get(store));
		set([]);
	};

	const removeVisited = () => {
		trackUserAction('readlater_remove_read');
		sortOrRevertStore(removeItems(get(store).filter((item) => item.visited)), get(store));
		update((items) => items.filter((item) => !item.visited));
	};

	// Chronologically sorts list or reverts to previous state if an error occurs
	const sortOrRevertStore = (action: Promise<ListItem[]>, previousState) => {
		action
			.then((res) => {
				if (!Array.isArray(res)) {
					throw new Error();
				}
				set(res.sort((a, z) => Number(a.createdTimestamp) - Number(z.createdTimestamp)));
			})
			.catch(() => {
				set(previousState);
			});
	};

	// This function injects event listeners for messages into the store.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const _sharedSubscribe = (run: Subscriber<any>, invalidate: Invalidator<any> = () => {}) => {
		const unsubscribe = subscribe(run, invalidate);

		window.addEventListener('message', onMessage); // buttons
		window.addEventListener('dst.user.loggedOut', onLogOut); // dst.user.loggedOut event
		channel.addEventListener('message', onBroadcastMessage); // tabs/windows

		return () => {
			window.removeEventListener('message', onMessage);
			window.removeEventListener('dst.user.loggedOut', onLogOut); // dst.user.loggedOut eventS
			channel.removeEventListener('message', onBroadcastMessage);

			unsubscribe();
		};
	};

	// Whenever the store updates, the new state is sent to all components in the window.
	// If the state change originates from the same tab, a message is sent into the broadcast channel,
	// to notify all other tabs/windows.
	store.subscribe((value) => {
		if (!initialised && shouldFetch(listName, isManualReload)) {
			initialised = true;
			getItems().then((res) => {
				if (!Array.isArray(res)) {
					log.error('Error fetching items');
					return;
				}

				sessionStorage.setItem(`${listName}-timestamp`, Date.now().toString());

				// Assume that the feature is not used, if an empty list is returned. Create entry in sessionStorage.
				if (res.length === 0) {
					sessionStorage.setItem(`${listName}-user`, 'false');
				}

				set(res.sort((a, z) => Number(a.createdTimestamp) - Number(z.createdTimestamp)));
			});
		}

		if (Array.isArray(value)) {
			sessionStorage.setItem(listName, JSON.stringify(value));
		}

		window.postMessage({ action: 'update', items: get(store) });

		// If the syncState and the new state are equal, the message originates from a different tab/window
		// which means that no message should be sent to the broadcast channel, to avoid loops.
		if (arraysAreEqual(value, syncState)) {
			return;
		}

		channel.postMessage(value);
	});

	return {
		subscribe: _sharedSubscribe,
		set,
		update,
	};
}

export default (listName: string, isReload: boolean) => readonly(createStore(listName, isReload));
